import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { JobPositionForCandidateDto } from '@pflegia/data-access';
import ContainerSection from '@pflegia/ui/shared/container-section';
import { LoadingCardsContainer } from '@pflegia/ui/shared/job-board/LoadingCardsContainer';

import { JobDetailsProvider } from '@shared/context/job-details.context';

import JobGoneSuggestions from './JobGoneSuggestions';

const JobPausedWithSuggestions = dynamic(() => import('@domains/job-detail/components/job-paused/JobPausedWithSuggestions.component'), {
  ssr: false,
});

export interface JobGonePageProps {
  suggestedJobs: JobPositionForCandidateDto[];
}

/**
 * JobGonePageContent Component
 *
 * Displays suggested jobs that were fetched on the server.
 */
const JobGonePageContent = ({ suggestedJobs }: { suggestedJobs: JobPositionForCandidateDto[] }): React.JSX.Element => {
  // Check if there are jobs available
  const hasJobs = suggestedJobs.length > 0;

  return (
    <JobDetailsProvider>
      <div className='flex-grow bg-background-secondary'>
        {/* Show suggestions header - pass jobsAvailable based on query results */}
        <JobPausedWithSuggestions jobsAvailable={hasJobs} />

        <div className='mb-6'>
          <ContainerSection>
            <JobGoneSuggestions suggestedJobs={suggestedJobs} />
          </ContainerSection>
        </div>
      </div>
    </JobDetailsProvider>
  );
};

const DynamicJobGonePageContent = dynamic(() => Promise.resolve(JobGonePageContent), {
  ssr: false,
  loading: () => (
    <div className='flex-grow bg-background-secondary'>
      <div className='mt-6'>
        <LoadingCardsContainer slimmed />
      </div>
    </div>
  ),
});

/**
 * JobGonePage Component
 *
 * Displays a fallback page when a job position is no longer available (410 Gone).
 * This page returns a 410 Gone HTTP status to indicate that the resource
 * is permanently unavailable.
 *
 * The page includes:
 * - JobPausedWithSuggestions component showing suggestions header
 * - JobPositionList showing similar job positions based on paused job's filters
 */
export const JobGonePage = ({ suggestedJobs }: JobGonePageProps): React.JSX.Element => {
  const { t } = useTranslation('common');

  return (
    <>
      <Head>
        <title>{t('errors.jobGone.title')}</title>
      </Head>
      <DynamicJobGonePageContent suggestedJobs={suggestedJobs} />
    </>
  );
};

export default JobGonePage;
